import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { ChangeEvent, useContext, useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import TooltipDialog from '../../components/dialog/tooltip/TooltipDialog'
import IconProvider from '../../components/icons/IconProvider'
import { Illustration } from '../../components/image/Illustration'
import { ProgressContext } from '../../context/ProgressProvider'
import { ISimulatorContext, SimulatorContext } from '../../context/SimulatorProvider'
import { StepperContext } from '../../context/StepperProvider'
import { Constants } from '../../data/constants'
import { Product, hasAvailableAsset } from '../../data/product'
import { useProductsQuery } from '../../hooks/useProductsQuery'
import './StationAssetsForm.css'
import useGameLink from '../../hooks/useGameLink'

function StationAssetsForm() {
  const { nextPage, setNextDisabled, isPreviousMove } = useContext(StepperContext)
  const { GetGameMsg } = useGameLink()
  let { placeId } = useParams()
  placeId = placeId?.replace('-', '')?.replace('_', '').toUpperCase()

  const { t } = useTranslation()
  const codeTotalRequiredLength = 6
  const dialogContent = [
    [
      t('station-assets-form.dialog-title'),
      <Trans i18nKey="station-assets-form.dialog-content-text" />,
      '/assets/illustration-1.svg'
    ]
  ]
  // const { setNextDisabled } = useContext(StepperContext)
  const { setIsLoading } = useContext(ProgressContext)
  const { codeTotal, setCodeTotal } = useContext(SimulatorContext) as ISimulatorContext
  const firstRender = useRef(true)
  const [localCodeTotal, setLocalCodeTotal] = useState(
    placeId?.replace(Constants.CODE_PREFIX, '') || codeTotal.replace(Constants.CODE_PREFIX, '')
  )
  const [validCodeTotal, setValidCodeTotal] = useState(false)
  const [productsAvailable, setProductsAvailable] = useState(false)
  const [disabledCodeTotal, setDisabledCodeTotal] = useState(false)
  const [codeTotalError, setCodeTotalError] = useState(false)
  const productDetails = useProductsQuery(localCodeTotal)

  useEffect(() => {
    if (firstRender.current) {
      setNextDisabled(true)
      firstRender.current = false
    }
  })

  useEffect(() => {
    if (productDetails.isSuccess && productDetails.data) {
      const productDetailsData = productDetails.data

      productDetailsData.products.forEach((product: Product) => {
        if (hasAvailableAsset(product)) {
          setProductsAvailable(true)
          setNextDisabled(false)
        }
      })

      if (typeof localCodeTotal !== 'undefined') {
        setCodeTotal(Constants.CODE_PREFIX + localCodeTotal)
      }

      setCodeTotalError(false)
      setValidCodeTotal(true)
      setNextDisabled(false)
      setDisabledCodeTotal(false)
      setIsLoading(false)
      if (!isPreviousMove && placeId !== null && placeId !== undefined && placeId !== '') {
        setTimeout(() => {
          nextPage()
        }, 250)
      }
    }
  }, [productDetails.isSuccess])

  useEffect(() => {
    if (productDetails.isError) {
      setNextDisabled(true)
      setProductsAvailable(false)
      setDisabledCodeTotal(false)
      setCodeTotalError(true)
      setValidCodeTotal(false)
      setIsLoading(false)
    }
  }, [productDetails.isError])

  const onCodeTotalChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value

    if (inputValue.length > codeTotalRequiredLength) {
      return
    } else if (inputValue.length === codeTotalRequiredLength && !productDetails.isError) {
      setIsLoading(true)
      setDisabledCodeTotal(true)
    }

    setLocalCodeTotal(inputValue)
    setNextDisabled(true)
    setProductsAvailable(false)
    setValidCodeTotal(false)
    setCodeTotalError(false)
  }

  const getHelperText = () => {
    if (!validCodeTotal) {
      if (codeTotalError) {
        return (
          <>
            <strong>{t('station-assets-form.input-label-error.1')}</strong>
            <br />
            <span>{t('station-assets-form.input-label-error.2')}</span>
          </>
        )
      }

      return <Trans i18nKey="station-assets-form.input-label"></Trans>
    }
  }

  return (
    <div className="station-assets-form-container">
      <Typography variant="body1">{t('station-assets-form.body1')}</Typography>
      <Typography variant="body2">{t('station-assets-form.body2')}</Typography>
      <div className="custom-input-container">
        <TextField
          className="custom-input-width"
          value={localCodeTotal}
          variant="outlined"
          type="number"
          size="small"
          InputProps={{ startAdornment: Constants.CODE_PREFIX, className: "input-nf" }}
          disabled={disabledCodeTotal}
          error={codeTotalError}
          onChange={onCodeTotalChange}
          helperText={getHelperText()}
          autoFocus
          required
        />
        <div className="tooltip-dialog-button">
          <TooltipDialog
            button={<IconProvider id="question_mark" color="black-filter" size="small" />}
            content={dialogContent}
            steps={1}
            action={t('station-assets-form.dialog-action')}
          />
        </div>
        {validCodeTotal && productDetails.data?.products && productsAvailable && (
          <div className="custom-input-feedback-container">
            <span className="valid-feedback">{t('station-assets-form.input-label-valid.1')}</span>
            <span className="valid-feedback">{t('station-assets-form.input-label-valid.2')}</span>
          </div>
        )}
        {validCodeTotal && productDetails.data && !productsAvailable && (
          <Typography variant="body1">{t('station-assets-form.no-product-available')}</Typography>
        )}
      </div>
      <div className="illustration-container">
        <Illustration
          src={process.env.PUBLIC_URL + '/assets/illustration-1.svg'}
          alt="Electic vehicle charging station code explanation"
          className="illustration-size"
        />
        {GetGameMsg()}
      </div>
    </div>
  )
}

export default StationAssetsForm
